import { BooleanField, Button, Datagrid, DateField, Edit, Filter, NumberField, NumberInput, ReferenceField, ReferenceInput, ReferenceManyField, SearchInput, SelectField, SelectInput, Show, SimpleForm, Tab, TabbedShowLayout, TextField, useListContext, useRecordContext } from "react-admin";
import { BillingDialog, List, Permission } from '../components';
import { PERMISSIONS } from "../constants";
import { getChainId, getCompanyId } from "../utils/utils";
import { useState } from "react";

const SubCompanyBillingEdit = () => {
  return (
    <Edit title="Editar Pagamento">
      <SimpleForm sanitizeEmptyValues defaultValues={{ companyId: getCompanyId(), chainId: getChainId() }}>
        <ReferenceInput source="companyId" reference="companies" disabled={!!getCompanyId()}>
          <SelectInput label="Empresa" optionText="name" sx={{ minWidth: 250 }} />
        </ReferenceInput>
        <ReferenceInput source="chainId" reference="chains" disabled={!!getChainId()}>
          <SelectInput label="Rede" optionText="name" sx={{ minWidth: 250 }} />
        </ReferenceInput>
        <NumberInput source="value" label="Valor" />
      </SimpleForm>
    </Edit>
  );
};

const ListFilters = (props) => {
  const { filterValues } = useListContext()
  return (
    <Filter {...props}>
      <SearchInput source="search" alwaysOn />
      {
        Permission({
          permission: PERMISSIONS.ADMIN,
          children: (
            <ReferenceInput source="companyId" reference="companies" sort={{ field: 'name', order: 'ASC' }} perPage={null} alwaysOn>
              <SelectInput emptyText="ra.action.clear_input_value" label="Grupo de Transportadora" optionText="name" sx={{ minWidth: 250 }} disabled={filterValues.chainId} />
            </ReferenceInput>
          )
        })
      }
      {
        Permission({
          permission: PERMISSIONS.ADMIN,
          children: (
            <ReferenceInput source="chainId" reference="chains" sort={{ field: 'name', order: 'ASC' }} perPage={false} alwaysOn>
              <SelectInput emptyText="ra.action.clear_input_value" label="Rede" optionText="name" sx={{ minWidth: 250 }} disabled={filterValues.companyId} />
            </ReferenceInput>
          )
        })
      }
    </Filter>
  );
}

const ModalSubCompanyBilling = ({ openDialog, handleCloseDialog }) => (
  <BillingDialog
    open={openDialog}
    onClose={handleCloseDialog}
    title="Confirmar Pagamento"
    errorMessage="Erro ao confirmar pagamento"
    successMessage="Pagamento confirmado com sucesso"
  />
);

const SubCompanyBilling = (props) => {

  const WrapperButton = ({ label }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const record = useRecordContext();

    const handleOpenDialog = (e) => {
      e?.stopPropagation();
      setOpenDialog(true);
    }

    const handleCloseDialog = (e) => {
      e?.stopPropagation();
      setOpenDialog(false);
    }

    if (!record) return null;

    return (
      <>
        <Button
          label={label}
          onClick={handleOpenDialog}
          variant="outlined"
          disabled={record.status === "paid"}
        />
        <ModalSubCompanyBilling openDialog={openDialog} handleCloseDialog={handleCloseDialog} />
      </>
    )
  }

  return (
    <List
      {...props}
      title="Lista de Pagamentos"
      filters={<ListFilters />}
    >
      <Datagrid bulkActionButtons={false} rowClick="show" >
        <TextField source="numericId" label="Código" textAlign="center" />
        <ReferenceField source="subCompanyId" reference="sub-companies" label="Transportadora">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="chainId" reference="chains" label="Rede">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="stationId" reference="stations" label="Posto">
          <TextField source="name" />
        </ReferenceField>
        <NumberField
          source="value"
          label="Valor"
          locales="pt-BR"
          textAlign="center"
          emptyText="Não informado"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
        />
        <SelectField
          label="Status"
          source="status"
          choices={[{ label: 'Pago', value: 'paid' }, { label: 'Pendente', value: 'pending' }]}
          optionText="label"
          optionValue="value"
          textAlign="center"
        />
        <WrapperButton label="Quitar" />
        <DateField source="createdAt" label="Criado em" locales="pt-BR" />
        <DateField source="dueDate" label="Vencimento em" locales="pt-BR" />
      </Datagrid>
    </List>
  );
};

const SubCompanyBillingShow = () => (
  <Show title="Recebimento">
    <TabbedShowLayout>
      <Tab label="Detalhes">
        <TextField source="numericId" label="Código" />
        <ReferenceField source="companyId" reference="companies" label="Empresa">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="chainId" reference="chains" label="Rede">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="stationId" reference="stations" label="Posto">
          <TextField source="name" />
        </ReferenceField>
        <NumberField source="value" label="Valor" options={{ style: 'currency', currency: 'BRL' }} />
        <SelectField
          label="Status"
          source="status"
          choices={[{ label: 'Pago', value: 'paid' }, { label: 'Pendente', value: 'pending' }]}
          optionText="label"
          optionValue="value"
          textAlign="center"
        />
        <DateField source="createdAt" label="Criado em" locales="pt-BR" />
        <DateField source="dueDate" label="Vencimento em" locales="pt-BR" />
      </Tab>
      <Tab label="Abastecimentos">
        <ReferenceManyField
          reference="fillins"
          target="subCompanyBillingId"
          addLabel={false}
        >
          <Datagrid bulkActionButtons={false} rowClick="show">
            <ReferenceField source="stationId" reference="stations" label="Posto">
              <TextField source="name" />
            </ReferenceField>
            <ReferenceField
              label="Veículo"
              reference="vehicles"
              source="vehicleId"
              textAlign="center"
              link={false}
            >
              <TextField source="licensePlate" />
            </ReferenceField>
            <ReferenceField
              label="Combustível"
              reference="fuels"
              source="fuelId"
              textAlign="center"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
            <NumberField
              source="value"
              label="Valor"
              locales="pt-BR"
              options={{ style: 'currency', currency: 'BRL' }}
            />
            <DateField showTime source="receivedAt" label="Data" locales="pt-BR" />
            <BooleanField source="manuallyAdded" label="Manual?" />
          </Datagrid>
        </ReferenceManyField>
      </Tab>
    </TabbedShowLayout>
  </Show>
);

const resource = {
  edit: SubCompanyBillingEdit,
  list: SubCompanyBilling,
  show: SubCompanyBillingShow,
  name: 'sub-company-billings',
  permissions: [PERMISSIONS.LIST_SUB_COMPANY_BILLINGS]
};

export default resource;